<template>
  <div class="tbl-container">
    <!-- Filter Controls -->
    <div class="filter-container bg-white p-4 mb-4 rounded-lg">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-base font-medium text-gray-900">
          Filter Transactions
        </h3>
        <div class="text-base text-gray-600">
          {{ filteredTxArray.length }} of {{ txMeta.totalCount }} results
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Block Range Filter -->
        <div class="filter-group">
          <label class="block text-base font-medium text-gray-700 mb-1">Block Range</label>
          <div class="flex space-x-2">
            <input 
              v-model.number="filters.blockMin" 
              @input="applyFilters"
              type="number" 
              min="1"
              placeholder="Min Block"
              class="w-1/2 px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
            <input 
              v-model.number="filters.blockMax" 
              @input="applyFilters"
              type="number" 
              min="1"
              placeholder="Max Block"
              class="w-1/2 px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
          </div>
        </div>

        <!-- Date Range Filter -->
        <div class="filter-group">
          <label class="block text-base font-medium text-gray-700 mb-1">Date Range</label>
          <div class="flex space-x-2">
            <Datepicker 
              v-model="filters.dateFrom" 
              @update:modelValue="applyFilters"
              placeholder="From Date"
              :format="dateFormat"
              class="w-1/2"
              inputClassName="w-full px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Datepicker 
              v-model="filters.dateTo" 
              @update:modelValue="applyFilters"
              placeholder="To Date"
              :format="dateFormat"
              class="w-1/2"
              inputClassName="w-full px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- Type Filter -->
        <div class="filter-group">
          <label class="block text-base font-medium text-gray-700 mb-1">Type</label>
          <select 
            v-model="filters.type" 
            @change="applyFilters"
            class="w-full px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option
              v-for="type in availableTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
        </div>

        <!-- Status Filter -->
        <div class="filter-group">
          <label class="block text-base font-medium text-gray-700 mb-1">Status</label>
          <select 
            v-model="filters.status" 
            @change="applyFilters"
            class="w-full px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="valid">Valid</option>
            <option value="invalid">Invalid</option>
          </select>
        </div>

        <!-- From Address Filter -->
        <div class="filter-group">
          <label class="block text-base font-medium text-gray-700 mb-1">From Address</label>
          <input 
            v-model="filters.from" 
            @input="applyFilters"
            type="text" 
            placeholder="Enter from address"
            class="w-full px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
        </div>

        <!-- To Address Filter -->
        <div class="filter-group">
          <label class="block text-base font-medium text-gray-700 mb-1">To Address</label>
          <input 
            v-model="filters.to" 
            @input="applyFilters"
            type="text" 
            placeholder="Enter to address"
            class="w-full px-3 py-2 border border-[#CFCFCF] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
        </div>

        <!-- Clear Filters Button -->
        <div class="filter-group flex items-end">
          <button 
            @click="clearFilters"
            class="w-full px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <div class="px-[15px]">
      <table>
        <thead>
          <tr class="t-header text-blue-dark text-[18px]">
            <th class="text-base">RefHash</th>
            <th class="text-base">Type</th>
            <th class="text-base">Coin Amount (GEEQ)</th>
            <th class="text-base">Fee (GEEQ)</th>
            <th class="text-base">Block</th>
            <th class="text-base">Timestamp</th>
            <th class="text-base">From</th>
            <th class="text-base">To</th>
            <th class="text-base">Status</th>
            <th v-if="accountAddress" class="text-base">Outgoing / Incoming</th>
          </tr>
        </thead>
        
        <tr v-for="item in filteredTxArray" :key="item.rowKey" style="border-top: 1px solid #2699fb;">
          <td class="max-w-[200px] min-w-[200px]">
            <div class="align-middle text-blue-light flex items-start text-[#374151] font-jost-regular text-base">
              <span class="cursor-pointer" @click="toggleRow(item)">
                <i class="fa fa-minus text-[14px] text-blue-light" v-if="isExpanded(item)"></i>
                <i class="fa fa-plus text-[14px] text-blue-light" v-else></i>
              </span>

              <span class="align-middle text-blue-light break-all ml-[10px]">
                <div class="inline">
                  <router-link v-if="item && item.refHash" class="cursor-pointer align-middle text-blue-light break-all font-jost-regular text-base"
                    :to="{ name: 'transaction', params: { chainNumber: item.chainNumber || $route.params.chainNumber || $route.params.chain, txRefhash: item.refHash }}">
                    {{ !isExpanded(item) ? getShortenedText(item.refHash) : item.refHash }}
                  </router-link>
                  <CopyIcon v-if="isExpanded(item)" :isActive="checkIfIsEqual(item.refHash, item.rowKey)"
                    @click="copy(item.refHash, item.rowKey)" title="Copy hash"
                    class="min-w-[16px] h-[19px] inline-block cursor-pointer align-text-top ml-[10px]" />
                </div>
              </span>
            </div>
          </td>
          <td class="text-blue-dark max-w-[100px] min-w-[100px] align-middle text-[#374151] font-jost-regular text-base">
            <div class="flex items-start text-[#374151] font-jost-regular text-base">
              {{ getTypeLabel(item.type) }}
            </div>
          </td>
          <td class="text-blue-dark max-w-[150px] min-w-[150px] align-middle text-[#374151] font-jost-regular text-base">
            <div class="flex items-start text-[#374151] font-jost-regular text-base">
              {{ getCoinAmount(item) }}
            </div>
          </td>
          <td class="text-blue-dark max-w-[150px] min-w-[150px] align-middle text-[#374151] font-jost-regular text-base">
            <div class="flex items-start text-[#374151] font-jost-regular text-base">
              {{ getFee(item) }}
            </div>
          </td>
          <td class="align-middle max-w-[150px] min-w-[150px]">
            <div class="flex items-start text-[#374151] font-jost-regular text-base">
              <router-link v-if="item && item.block" class="text-blue-light cursor-pointer ml-[10px]"
                :to="{ name: 'block', params: { chainNumber: item.chainNumber || $route.params.chainNumber || $route.params.chain, blockNumber: item.block }}">
                {{ item.block }}
              </router-link>
            </div>
          </td>

          <td class="max-w-[200px] min-w-[200px] align-middle text-[#374151] font-jost-regular text-base">
            {{ getTxCreatedAt(item.createdAt) }}
          </td>
          <td class="max-w-[200px] min-w-[200px] align-middle">
            <router-link
              v-if="item.from"
              :to="{ name: 'account', params: { chainNumber: item.chainNumber || $route.params.chainNumber || $route.params.chain, address: item.from } }"
              class="align-middle text-blue-light break-all font-jost-regular text-base"
            >
              <div class="inline">
                {{ !isExpanded(item) ? getShortenedText(item.from) : item.from }}
              </div>
            </router-link>
            <div
              v-else
              class="inline align-middle text-[#374151] font-jost-regular text-base"
            >
              {{ item.from || '-' }}
            </div>
            <CopyIcon v-if="isExpanded(item) && item.from" :isActive="checkIfIsEqual(item.from, item.rowKey + 'from')"
              @click="copy(item.from, item.rowKey + 'from')" title="Copy hash"
              class="min-w-[16px] h-[19px] inline-block cursor-pointer align-text-top ml-[10px]" />
          </td>
          <td class="max-w-[200px] min-w-[200px] align-middle">
            <router-link
              v-if="item.to"
              :to="{ name: 'account', params: { chainNumber: item.chainNumber || $route.params.chainNumber || $route.params.chain, address: item.to } }"
              class="align-middle text-blue-light break-all font-jost-regular text-base"
            >
              <div class="inline">
                {{ !isExpanded(item) ? getShortenedText(item.to) : item.to }}
              </div>
            </router-link>
            <div
              v-else
              class="inline align-middle text-[#374151] font-jost-regular text-base"
            >
              {{ item.to || '-' }}
            </div>
            <CopyIcon v-if="isExpanded(item) && item.to" :isActive="checkIfIsEqual(item.to, item.rowKey + 'to')"
              @click="copy(item.to, item.rowKey + 'to')" title="Copy hash"
              class="min-w-[16px] h-[19px] inline-block cursor-pointer align-text-top ml-[10px]" />
          </td>
          <td class="text-blue-dark text-[18px] max-w-[100px] min-w-[100px]">
            <div class="flex items-center gap-3">
              <span class="px-2 py-1 text-base font-medium rounded uppercase"
                  :class="item.status === 'valid' ? 'bg-green-100 text-green-800' :
                    item.status === 'invalid' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'">
                {{ item.status || 'PENDING' }}
              </span>
            </div>
          </td>
          <td v-if="accountAddress" class="max-w-[80px] min-w-[80px] align-middle">
            <div class="flex justify-center items-center">
              <img
                v-if="isIncoming(item)"
                src="/images/icons/received-txs.png"
                title="Incoming"
              />
              <img
                v-else
                src="/images/icons/sent-txs.png"
                title="Outgoing"
              />
            </div>
          </td>
        </tr>
        <template v-if="filteredTxArray.length < maxTableRows">
          <tr v-for="index in (maxTableRows - filteredTxArray.length)" :key="index" class="border-t border-blue-row h-[63px]">
            <template v-for="i in colsCount" :key="i">
              <td></td>
            </template>
          </tr>
        </template>
      </table>
    </div>
  </div>
</template>

<script>
import string from '@/helpers/string';
import { maxTableRows } from '@/utils/pagination';
import CopyIcon from '@/components/svg/CopyIcon';
import Datepicker from 'vue3-datepicker';

import moment from 'moment';

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'attestCommit', label: 'Receipt ID' },
  { value: 'coin', label: 'Coin' },
  { value: 'proxyCreate', label: 'Authority Create' },
  { value: 'nftMint', label: 'Digital Asset' },
];

export default {
  name: 'SearchableTable',
  emits: ['copied', 'filtersChanged'],
  components: {
    CopyIcon,
    Datepicker,
  },
  computed: {
    txArray() {
      const transactions = Array.isArray(this.transactions)
        ? this.transactions
        : Array.isArray(this.transactions?.items)
          ? this.transactions.items
        : Array.isArray(this.transactions?.result)
          ? this.transactions.result
          : [];

      return transactions.map((tx, index) => ({
        ...tx,
        rowKey: this.getRowKey(tx, index),
      }));
    },
    filteredTxArray() {
      let filtered = [...this.txArray];

      // // Type filter
      // if (this.filters.type) {
      //   filtered = filtered.filter(tx => tx.type === this.filters.type);
      // }

      // // Block range filter
      // if (this.filters.blockMin !== null && this.filters.blockMin !== '') {
      //   filtered = filtered.filter(tx => tx.block >= this.filters.blockMin);
      // }
      // if (this.filters.blockMax !== null && this.filters.blockMax !== '') {
      //   filtered = filtered.filter(tx => tx.block <= this.filters.blockMax);
      // }

      // // Date range filter
      // if (this.filters.dateFrom) {
      //   const fromDate = moment(this.filters.dateFrom).startOf('day');
      //   filtered = filtered.filter(tx => moment(tx.createdAt).isSameOrAfter(fromDate));
      // }
      // if (this.filters.dateTo) {
      //   const toDate = moment(this.filters.dateTo).endOf('day');
      //   filtered = filtered.filter(tx => moment(tx.createdAt).isSameOrBefore(toDate));
      // }

      // // From address filter
      // if (this.filters.from) {
      //   filtered = filtered.filter(tx => 
      //     tx.from && tx.from.toLowerCase().includes(this.filters.from.toLowerCase())
      //   );
      // }

      // // To address filter
      // if (this.filters.to) {
      //   filtered = filtered.filter(tx => 
      //     tx.to && tx.to.toLowerCase().includes(this.filters.to.toLowerCase())
      //   );
      // }

      // // Status filter
      // if (this.filters.status) {
      //   filtered = filtered.filter(tx => tx.status === this.filters.status);
      // }

      return filtered;
    },
    availableTypes() {
      return this.transactionTypes;
    },
    availableTypeValues() {
      return this.transactionTypes.map(type => type.value);
    },
    availableStatuses() {
      return ['valid', 'invalid'];
    },
    txMeta() {
      return {
        totalCount: this.transactions?.totalCount ?? this.txArray.length,
      };
    },
  },
  props: {
    transactions: {
      type: Object,
      required: true,
      default: () => ({ totalCount: 0, items: []}),
    },
    copied: {
      type: String,
      default: '',
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    accountAddress: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      maxTableRows,
      colsCount: this.accountAddress ? 10 : 9,
      balanceDivisor: parseInt(process.env.VUE_APP_BALANCE_DIVISOR) || 1000000000,
      filters: {
        type: '',
        blockMin: null,
        blockMax: null,
        dateFrom: null,
        dateTo: null,
        from: '',
        to: '',
        status: ''
      },
      dateFormat: 'yyyy-MM-dd',
      transactionTypes: [...TRANSACTION_TYPE_OPTIONS],
      initialLoad: true,
      expandedRows: {}
    };
  },
  created() {
    this.applyInitialFilters(this.initialFilters);
  },
  watch: {
    initialFilters: {
      deep: true,
      handler(newValue) {
        this.applyInitialFilters(newValue);
      }
    }
  },
  methods: {
    getRowKey(tx, index = 0) {
      return tx?.refHash || tx?.id || `${tx?.chainNumber || ''}-${tx?.block || ''}-${tx?.createdAt || ''}-${index}`;
    },
    isExpanded(item) {
      return Boolean(this.expandedRows[item.rowKey]);
    },
    toggleRow(item) {
      const key = item.rowKey;
      this.expandedRows = {
        ...this.expandedRows,
        [key]: !this.expandedRows[key],
      };
    },
    getDefaultFilters() {
      return {
        type: '',
        blockMin: null,
        blockMax: null,
        dateFrom: null,
        dateTo: null,
        from: '',
        to: '',
        status: ''
      };
    },
    applyInitialFilters(initialFilters = {}) {
      this.filters = {
        ...this.getDefaultFilters(),
        ...initialFilters
      };
    },
    getTypeLabel(type) {
      const matchedType = this.transactionTypes.find(item => item.value === type);
      return matchedType?.label || type;
    },
    getBalance(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return amount;
      }

      const parsedAmount = Number(amount);
      if (Number.isNaN(parsedAmount) || !this.balanceDivisor) {
        return amount;
      }

      return parsedAmount / this.balanceDivisor;
    },
    getCoinAmount(item) {
      const values = Array.isArray(item.values) ? item.values : [];
      const coinValue = values.find(value => value && value.type === 'coin');
      if (!coinValue || coinValue.value === null || coinValue.value === undefined || coinValue.value === '') {
        return '-';
      }

      return `${this.getBalance(coinValue.value)}`;
    },
    getFee(item) {
      const fee = item?.fee
        ?? item?.uat?.overview?.fee
        ?? item?.uat?.uatData?.fee;
      if (fee === null || fee === undefined || fee === '') {
        return '-';
      }

      return `${this.getBalance(fee)}`;
    },
    getShortenedText(str) {
      if (!str) {
        return '';
      }
      return string.getShortenHash(str)
    },
    checkIfIsEqual(str, uId = 0) {
      return string.isTextEqualTo(str, this.copied, uId);
    },
    copy(hash, uId = 0) {
      string.copyToClipboard(hash);
      this.$emit('copied', hash + uId);
    },
    getTxCreatedAt(createdAt) {
      if (!createdAt) {
        return '';
      }
      return moment(createdAt).format('YYYY-MM-DD HH:mm:ss');
    },
    applyFilters() {
      // Debounce the filter application to avoid excessive updates
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        const typesForRequest = this.filters.type
          ? [this.filters.type]
          : [...this.availableTypeValues];
        const statusesForRequest = this.filters.status
          ? [this.filters.status]
          : [...this.availableStatuses];

        this.$emit('filtersChanged', {
          ...this.filters,
          type: typesForRequest,
          status: statusesForRequest,
          resultCount: this.filteredTxArray.length
        });
      }, 300);
    },
    clearFilters() {
      this.filters = this.getDefaultFilters();
      this.applyFilters();
    },
    isIncoming(item) {
      if (!this.accountAddress) return false;
      const addr = this.accountAddress.toLowerCase();
      return (item.to && item.to.toLowerCase() === addr) ||
             (item.owner && item.owner.toLowerCase() === addr);
    }
  },
  beforeUnmount() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  }
};
</script>

<style scoped>
.filter-container {
  background-color: #ffffff;
  /* border: 1px solid #e5e7eb; */
}

.filter-group label {
  color: #374151;
  font-weight: 500;
}

.filter-group input,
.filter-group select {
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filter-group input:focus,
.filter-group select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Custom datepicker styling */
:deep(.dp__input) {
  width: 100% !important;
  padding: 0.5rem 0.75rem !important;
  border: 1px solid #d1d5db !important;
  border-radius: 0.375rem !important;
  font-size: 0.875rem !important;
}

:deep(.dp__input:focus) {
  outline: none !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}
</style>
